/* dev-launch-popup.js */
(function () {
  'use strict';

  var DAY_MS = 86400000;

  class LaunchPopup extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.overlay = this.querySelector('[data-lp-overlay]');
      this.dialog = this.querySelector('.launch-popup__dialog');
      this.storageKey = this.dataset.storageKey || 'dev-launch-popup';
      this.delay = parseInt(this.dataset.delay, 10) || 0;
      this.frequencyDays = parseInt(this.dataset.frequencyDays, 10);
      if (isNaN(this.frequencyDays)) this.frequencyDays = 14;
      this.designMode = !!(window.Shopify && window.Shopify.designMode);

      this._onKeydown = this._onKeydown.bind(this);
      this.close = this.close.bind(this);

      if (this.overlay) this.overlay.addEventListener('click', this.close);
      this.querySelectorAll('[data-lp-close]').forEach(function (btn) {
        btn.addEventListener('click', this.close);
      }, this);

      // Shopify shares `form.posted_successfully?` across every {% form 'customer' %}
      // on the page, so submitting the footer/newsletter form would otherwise make
      // this popup pop its success message too. Flag when THIS form is really submitted
      // (the submit event only fires once the browser's required/email validation
      // passes — an empty submit never reaches it) and require that flag on reload.
      this.submitFlagKey = this.storageKey + '--submitting';
      // Namespaced per form: the hero waitlist captures the same signup on this
      // same page and keys its own Lead separately.
      this.leadKey = 'rgd:lead-email--launch-popup';
      var lpForm = this.querySelector('.launch-popup__form');
      if (lpForm) {
        lpForm.addEventListener('submit', function () {
          try {
            window.sessionStorage.setItem(this.submitFlagKey, '1');
          } catch (e) {
            /* storage unavailable — fail open */
          }
          var input = lpForm.querySelector('[name="contact[email]"]');
          if (input) window.RGDLead.remember(this.leadKey, input.value);
        }.bind(this));
      }

      // Theme editor: preview on select, ignore the frequency cap entirely.
      if (this.designMode) {
        var sectionId = this.id.replace('LaunchPopup-', '');
        document.addEventListener('shopify:section:select', function (e) {
          if (e.detail && e.detail.sectionId === sectionId) this.open();
        }.bind(this));
        document.addEventListener('shopify:section:deselect', function (e) {
          if (e.detail && e.detail.sectionId === sectionId) this.close(true);
        }.bind(this));
        return;
      }

      // After a submit the page reloads — reopen to show the success/error message.
      // Detected from the rendered DOM (the `form` object is only in scope inside {% form %}).
      if (this.querySelector('.launch-popup__message--success, .launch-popup__message--error')) {
        // Only act on the message if THIS popup's form was the one submitted.
        var justSubmitted = false;
        try {
          justSubmitted = window.sessionStorage.getItem(this.submitFlagKey) === '1';
          window.sessionStorage.removeItem(this.submitFlagKey);
        } catch (e) {
          /* storage unavailable — fail open */
        }
        if (!justSubmitted) {
          // The other {% form 'customer' %} on this page — the hero waitlist —
          // is what succeeded. Its own success message is already showing, so
          // stay shut; but record it, or we'd pop up again on their next visit
          // asking for an address they just gave us.
          if (this.querySelector('.launch-popup__message--success')) this._rememberSubscribed();
          return;
        }

        if (this.querySelector('.launch-popup__message--success')) {
          this._remember();
          this._rememberSubscribed();
          // Same capture as the hero waitlist, so it reports the same way.
          window.RGDLead.track(this.leadKey, 'Launch Popup');
          // Collapse the popup to show only the success message.
          this.classList.add('is-submitted');
        }
        this.open();
        return;
      }

      if (this._isSubscribed() || this._isDismissed()) return;

      this._timer = window.setTimeout(this.open.bind(this), this.delay);
    }

    _isDismissed() {
      if (this.frequencyDays <= 0) return false;
      try {
        var ts = window.localStorage.getItem(this.storageKey);
        if (!ts) return false;
        return Date.now() - parseInt(ts, 10) < this.frequencyDays * DAY_MS;
      } catch (e) {
        return false;
      }
    }

    _remember() {
      try {
        window.localStorage.setItem(this.storageKey, String(Date.now()));
      } catch (e) {
        /* storage unavailable — fail open */
      }
    }

    /* Successful signup — never auto-show again, regardless of frequency. */
    _isSubscribed() {
      try {
        return window.localStorage.getItem(this.storageKey + '--subscribed') === '1';
      } catch (e) {
        return false;
      }
    }

    _rememberSubscribed() {
      try {
        window.localStorage.setItem(this.storageKey + '--subscribed', '1');
      } catch (e) {
        /* storage unavailable — fail open */
      }
    }

    open() {
      if (this._isOpen) return;
      this._isOpen = true;
      this._lastFocused = document.activeElement;

      this.removeAttribute('hidden');
      // Force a reflow so the transition runs from the hidden state.
      void this.offsetWidth;
      this.classList.add('is-open');

      document.documentElement.style.overflow = 'hidden';
      document.addEventListener('keydown', this._onKeydown);

      var field = this.querySelector('.launch-popup__input input');
      var target = field || this.querySelector('[data-lp-close]');
      if (target) {
        window.setTimeout(function () {
          target.focus({ preventScroll: true });
        }, 80);
      }
    }

    close(skipRemember) {
      window.clearTimeout(this._timer);
      if (!this._isOpen) {
        this.setAttribute('hidden', '');
        return;
      }
      this._isOpen = false;

      this.classList.remove('is-open');
      document.documentElement.style.overflow = '';
      document.removeEventListener('keydown', this._onKeydown);

      if (skipRemember !== true) this._remember();

      var self = this;
      var hide = function () {
        self.setAttribute('hidden', '');
        self.dialog.removeEventListener('transitionend', hide);
      };
      this.dialog.addEventListener('transitionend', hide);
      window.setTimeout(function () {
        if (!self._isOpen) self.setAttribute('hidden', '');
      }, 450);

      if (this._lastFocused && typeof this._lastFocused.focus === 'function') {
        this._lastFocused.focus();
      }
    }

    _onKeydown(e) {
      if (e.key === 'Escape') {
        this.close();
      } else if (e.key === 'Tab') {
        this._trapFocus(e);
      }
    }

    _trapFocus(e) {
      var focusable = this.dialog.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  if (!window.customElements.get('launch-popup')) {
    window.customElements.define('launch-popup', LaunchPopup);
  }
})();
